if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this._soundPlaybackUntil = 0;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

          window.requestAnimationFrame(() => {
            const activeSlide = this.elements.viewer.querySelector('li.is-active');
            if (activeSlide) this.playActiveMedia(activeSlide);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.addEventListener('click', (event) => {
          const button = event.target.closest('.thumbnail-list button');
          if (!button) return;
          const mediaToSwitch = button.closest('[data-target]');
          if (!mediaToSwitch || !this.elements.thumbnails.contains(mediaToSwitch)) return;
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (currentElement) this.playActiveMedia(currentElement);

        if (!this.elements.thumbnails || !currentElement) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, playOptions = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia, playOptions);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveMediaWithSound(mediaId) {
        // Slider scroll fires debounced slideChanged → playActiveMedia (muted). Keep sound briefly.
        this._soundPlaybackUntil = Date.now() + 1500;
        this.setActiveMedia(mediaId, false, { withSound: true });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !position) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem, playOptions = {}) {
        const autoplayWithSound = this.dataset.autoplayVideoWithSound === 'true';
        const withSound =
          autoplayWithSound ||
          playOptions.withSound === true ||
          (this._soundPlaybackUntil && Date.now() < this._soundPlaybackUntil);

        if (playOptions.withSound === true) {
          this._soundPlaybackUntil = Date.now() + 1500;
        }

        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);

        const variantMetafieldVideo = activeItem.querySelector('video.nw-variant-mf-video');
        if (variantMetafieldVideo) {
          variantMetafieldVideo.muted = !withSound;
          if (withSound) {
            variantMetafieldVideo.volume = 1;
          }
          const playPromise = variantMetafieldVideo.play();
          if (playPromise !== undefined) playPromise.catch(() => {});
          return;
        }

        if (withSound) {
          this.playMediaWithSound(activeItem);
        }
      }

      playMediaWithSound(activeItem) {
        const attempt = () => {
          const video = activeItem.querySelector('.deferred-media video, deferred-media video, video');
          if (video) {
            video.muted = false;
            video.volume = 1;
            const playPromise = video.play();
            if (playPromise !== undefined) playPromise.catch(() => {});
            return true;
          }

          const youtube = activeItem.querySelector('iframe.js-youtube');
          if (youtube?.contentWindow) {
            youtube.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
            youtube.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            return true;
          }

          const vimeo = activeItem.querySelector('iframe.js-vimeo');
          if (vimeo?.contentWindow) {
            vimeo.contentWindow.postMessage('{"method":"setVolume","value":1}', '*');
            vimeo.contentWindow.postMessage('{"method":"play"}', '*');
            return true;
          }

          return false;
        };

        if (attempt()) return;

        let attempts = 0;
        const timer = window.setInterval(() => {
          if (attempt() || ++attempts >= 25) {
            window.clearInterval(timer);
          }
        }, 100);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
